import React, { useState } from 'react';
import _ from 'lodash';
import { Typography, Button, Modal, TextArea, TextField, Banner } from '@scale/scaleui';
import { AuditPermissionRole } from '@scale/shared/taskerScreening/types';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';

interface TaskerScreeningAuditorPermissionBenchmarkModalProps {
  role: AuditPermissionRole;
  open: boolean;
  onClose: () => void;
  onSave: (id: string, benchmarkWall: number, benchmarkAccuracyBar: number) => void;
}

const PaddedHeader = ({ title }: { title: string }) => {
  return (
    <div className="mt-4 mb-2">
      <Typography.H5>{title}</Typography.H5>
    </div>
  );
};

const TaskerScreeningAuditorPermissionBenchmarkContents: React.FC<
  TaskerScreeningAuditorPermissionBenchmarkModalProps
> = ({ role, onSave }) => {
  const [benchmarkWall, setBenchmarkWall] = useState<number | undefined>(role.benchmarkWall);
  const [benchmarkAccuracyBar, setBenchmarkAccuracyBar] = useState<number | undefined>(
    role.benchmarkAccuracyBar,
  );
  const saveDisabled =
    !_.isNumber(benchmarkWall) ||
    !_.isNumber(benchmarkAccuracyBar) ||
    benchmarkAccuracyBar < 0 ||
    benchmarkAccuracyBar > 1;
  return (
    <div className="scale-ui scale-theme scale-tailwind w-[600px]">
      <PaddedHeader title="Role Name" />
      <Typography.Paragraph2>{role.name}</Typography.Paragraph2>
      {role.description && (
        <div>
          {' '}
          <PaddedHeader title="Role Description" />
          <Typography.Paragraph2>{role.description}</Typography.Paragraph2>
        </div>
      )}
      <PaddedHeader title="Role Courses" />
      {role.courseIds.map(courseId => (
        <div key={courseId} className="w-full">
          <SmartLink key={courseId} type={INamedObjectTypes.Course} id={courseId} />
        </div>
      ))}
      <PaddedHeader title="Role Course Section Titles" />
      <Typography.Paragraph2>{role.sectionTitles.join(', ')}</Typography.Paragraph2>
      <PaddedHeader title="Currently Assigned Benchmarks" />
      <div className="mb-2">
        <Typography.Paragraph2>
          Use the "Screening Benchmarks" tab to assign enabled benchmarks to auditor permissions.
        </Typography.Paragraph2>
      </div>
      <TextArea
        id="benchmarksPreview"
        value={(role.benchmarksAssigned?.length ? role.benchmarksAssigned : ['none']).join(',\n')}
        disabled
      />
      <PaddedHeader title="Update Benchmark Wall" />
      <TextField
        id="wall"
        value={benchmarkWall}
        onChange={e => setBenchmarkWall(Number(e.target.value))}
        placeholder="Benchmark Wall"
        type="number"
      />
      <PaddedHeader title="Update Benchmark Accuracy Bar" />
      <div className="mb-2">
        <Typography.Paragraph2>Enter a decimal value between 0 and 1.</Typography.Paragraph2>
      </div>
      <TextField
        id="accuracy"
        value={benchmarkAccuracyBar}
        onChange={e => setBenchmarkAccuracyBar(Number(e.target.value))}
        placeholder="Benchmark Accuracy Bar"
        type="number"
      />
      {(benchmarkWall ?? 0) > (role.benchmarksAssigned?.length ?? 0) && (
        <Banner
          className="mb-2 mt-4"
          title="Benchmark Wall"
          subtitle="The benchmark wall is higher than the number of benchmarks assigned to this role. Auditors assigned to this permission will not be able to pass their benchmark wall. Make sure to navigate to the Screening Benchmarks tab to assign benchmarks to this permission."
          variant="warning"
          background="neutral"
        />
      )}
      <Button
        className="mt-4"
        onClick={() => onSave(role.id, benchmarkWall ?? 0, benchmarkAccuracyBar ?? 0)}
        variant="primary"
        disabled={saveDisabled}
      >
        Save Changes
      </Button>
    </div>
  );
};

const TaskerScreeningAuditorPermissionBenchmarkModal: React.FC<
  TaskerScreeningAuditorPermissionBenchmarkModalProps
> = ({ open, onClose, role, onSave }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      theme="scale-theme"
      title="Audit Permission Benchmark Settings"
      innerClassName="overflow-y-auto"
    >
      <TaskerScreeningAuditorPermissionBenchmarkContents
        open={open}
        onClose={onClose}
        role={role}
        onSave={onSave}
      />
    </Modal>
  );
};

export default TaskerScreeningAuditorPermissionBenchmarkModal;
